// Vendor Program v2 PR 2 — the Vendor portal "My performance" tab: the
// vendor's own borrower sign-off ratings. Overall tiles (x.x / 4 per area),
// a per-customer table, the monthly trend, and recent feedback quotes.
// Ratings only exist once borrowers sign off monthly reports, so the empty
// state explains the path there calmly.

import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import {
  EmptyState,
  SkeletonList,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import {
  portalVendorApi,
  type VendorPerformanceAreaDto,
  type VendorPerformanceSummaryDto,
} from "../api/vendor";

function rating(value: number | null | undefined): string {
  return value == null ? "—" : `${value.toFixed(1)} / 4`;
}

/** "June 2026" for an ISO month-start (UTC month boundaries). */
function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const AREA_TILES: { key: keyof VendorPerformanceAreaDto; label: string }[] = [
  { key: "overall", label: "Overall" },
  { key: "serviceQuality", label: "Service quality" },
  { key: "responsiveness", label: "Responsiveness" },
  { key: "satisfaction", label: "Satisfaction" },
];

export function VendorPerformancePortalTab() {
  const [summary, setSummary] = useState<VendorPerformanceSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalVendorApi.getPerformance();
        if (!cancelled) setSummary(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your ratings.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!summary) {
    return <SkeletonList data-testid="performance-loading" />;
  }
  if (summary.overall.signOffCount === 0) {
    return (
      <EmptyState
        icon={Star}
        title="No ratings yet"
        description="Ratings appear after borrowers sign off your monthly reports."
      />
    );
  }

  return (
    <section aria-label="My performance" className="flex flex-col gap-space-4">
      <div>
        <p className="text-caption text-gray-mid m-0 mb-space-2">
          Based on {summary.overall.signOffCount} borrower sign-off
          {summary.overall.signOffCount === 1 ? "" : "s"}
          {summary.overall.staffRecordedCount > 0
            ? ` — ${summary.overall.staffRecordedCount} recorded by Phoenix on the borrower's behalf`
            : ""}
          .
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3" data-testid="performance-tiles">
          {AREA_TILES.map((tile) => (
            <div
              key={tile.key}
              className="rounded-lg border border-border-light bg-white-pure p-space-3 flex flex-col gap-space-1"
            >
              <span className="text-caption text-gray-mid uppercase tracking-label">
                {tile.label}
              </span>
              <span className="font-display font-bold text-h3 tabular-nums">
                {rating(summary.overall[tile.key])}
              </span>
            </div>
          ))}
        </div>
      </div>

      {summary.perCustomer.length > 0 && (
        <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-2">
          <h3 className="font-display font-semibold text-h4 m-0">By client</h3>
          <Table data-testid="performance-per-customer">
            <THead>
              <TR>
                <TH>Client</TH>
                <TH>Your role</TH>
                <TH className="text-right">Overall</TH>
                <TH className="text-right">Service</TH>
                <TH className="text-right">Responsiveness</TH>
                <TH className="text-right">Satisfaction</TH>
                <TH className="text-right">Sign-offs</TH>
              </TR>
            </THead>
            <TBody>
              {summary.perCustomer.map((row) => (
                <TR key={row.loanId}>
                  <TD className="font-medium text-onyx">{row.loanNumber}</TD>
                  <TD>{row.roleOnLoan}</TD>
                  <TD className="text-right tabular-nums">{rating(row.ratings.overall)}</TD>
                  <TD className="text-right tabular-nums">{rating(row.ratings.serviceQuality)}</TD>
                  <TD className="text-right tabular-nums">{rating(row.ratings.responsiveness)}</TD>
                  <TD className="text-right tabular-nums">{rating(row.ratings.satisfaction)}</TD>
                  <TD className="text-right tabular-nums">{row.ratings.signOffCount}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      {summary.monthlyTrend.length > 0 && (
        <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-2">
          <h3 className="font-display font-semibold text-h4 m-0">Month by month</h3>
          <ul className="m-0 p-0 list-none flex flex-col" data-testid="performance-trend">
            {summary.monthlyTrend.map((point) => (
              <li
                key={point.month}
                className="flex items-baseline justify-between gap-space-2 text-small py-space-1 border-b border-border-light last:border-b-0"
              >
                <span>{monthLabel(point.month)}</span>
                <span className="tabular-nums">
                  {rating(point.overall)}
                  <span className="text-caption text-gray-mid">
                    {" "}
                    · {point.count} sign-off{point.count === 1 ? "" : "s"}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.recentFeedback.length > 0 && (
        <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-2">
          <h3 className="font-display font-semibold text-h4 m-0">Recent feedback</h3>
          <ul className="m-0 p-0 list-none flex flex-col gap-space-3" data-testid="performance-feedback">
            {summary.recentFeedback.map((item, index) => (
              <li key={`${item.loanNumber}-${item.periodStart}-${index}`}>
                <blockquote className="m-0 border-l-2 border-gold pl-space-3 text-small text-onyx">
                  “{item.feedback}”
                </blockquote>
                <p className="text-caption text-gray-mid m-0 mt-space-1">
                  {item.loanNumber} · {monthLabel(item.periodStart)} ·{" "}
                  {rating(item.overallRating)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
